import { axiosInstance } from "@src/api/axiosConfig";
import { axiosPath } from "@src/api/axiosPath";
import { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import qs from "qs";

// 관심지역 필터 타입
export interface IFavoriteRegionFilter {
    regions: string[];
    categories: string[];
    floor?: number | null; // 0: 지하, 1: 1층상가, 2: 2층상가, 3: 3층이상 (근린상가 선택 시). 미선택 시 null
}

export interface IPostFavoriteRegionBody {
    regions: string[];
    categories: string[];
    settingOrder: number;
    isFinish: boolean;
    sortType?: number;
    saleMethod?: number;
    floor?: number | null;
}

// 관심지역 매물 아이템
export interface IFavoriteRegionItem {
    itemId: string;
    subId: string;
    bidType: number;
    useDetail: string;
    skpCnt: number;
    image: string;
    expectedReturn: string;
    estPrice: number;
    minPrice: number;
    discountRate: string;
    dueDate: number;
    address: {
        addressTop: string;
        addressMid: string;
    };
}

export interface IFavoriteRegionList {
    cursor: string;
    totalCount: number;
    list: IFavoriteRegionItem[];
}

// 관심지역 매물 조회
export interface IFavoriteRegionItemListParams {
    regions: string;
    categories: string[];
    floor?: number | null;
    settingOrder: number;
    sortType?: number;
}

export interface IFavoriteRegionSettingItem {
    no: number;
    order: number;
    settingOrder?: number;
    itemFilter: {
        investmentPrice?: number;
        regions: string[];
        categories: string[];
        bidType?: number | null;
        saleMethod?: number;
        floor?: number | null;
        assetDetails?: number[] | null;
    };
    createdAt?: string;
    isFinish: boolean;
}

export interface IFavoriteRegionSettingList {
    list: IFavoriteRegionSettingItem[];
}

// 관심지역 매물 조회 (직접 호출)
export const getFavoriteRegionItems = async (params: IFavoriteRegionItemListParams): Promise<IFavoriteRegionList> => {
    const shouldSendFloor = params.categories?.includes("D02") && params.floor != null;
    const requestParams = {
        regions: params.regions || undefined,
        categories: params.categories?.length ? params.categories : undefined,
        sortType: params.sortType,
        floor: shouldSendFloor ? params.floor : undefined,
        limit: 10000,
    };
    const res = await axiosInstance
        .get(axiosPath.itemsFavoriteRegion, {
            params: requestParams,
            paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "brackets" }),
        })
        .then((res) => res.data)
        .catch((err) => {
            console.log("err>>>", err);
            throw err;
        });
    return res;
};

// 관심지역 매물 조회 (useQuery)
export const useFavoriteRegionItemList = (params: IFavoriteRegionItemListParams | null, enabled: boolean) => {
    return useQuery<IFavoriteRegionList>({
        queryKey: [
            "favoriteRegionItems",
            params?.settingOrder,
            params?.regions ?? "",
            params?.categories?.join(",") ?? "",
            params?.sortType,
            params?.categories?.includes("D02") ? params?.floor ?? null : null,
            9999,
        ],
        queryFn: async () => getFavoriteRegionItems(params!),
        enabled: enabled && !!params && (params.regions.length > 0 || params.categories.length > 0),
        refetchOnWindowFocus: false,
    });
};

// 관심지역 필터 등록/수정
export const usePostFavoriteRegionFilter = (
    onSuccess: (datum: any) => void = () => {},
    onError: (err: AxiosError) => void = () => {},
) => {
    return useMutation(
        async (params: IPostFavoriteRegionBody) => {
            const response = await axiosInstance.post(axiosPath.aiSettingFavoriteRegion, params);
            return response;
        },
        { onSuccess, onError },
    );
};

// 관심지역 필터 조회
export const useGetFavoriteRegionFilter = (enabled: boolean = false) => {
    return useQuery<IFavoriteRegionSettingList | null>(
        ["favoriteRegionFilter"],
        async () => {
            const result = await axiosInstance
                .get(axiosPath.aiSettingFavoriteRegion)
                .then((res) => res.data)
                .catch((err) => {
                    console.log("err>>>", err);
                    return null;
                });
            return result;
        },
        { enabled },
    );
};

// 관심지역 필터 설정 여부
export const useGetFavoriteRegionComplete = (enabled: boolean = false) => {
    return useQuery<{ complete: boolean }>(
        ["favoriteRegionComplete"],
        async () => {
            const result = await axiosInstance
                .get(axiosPath.aiSettingFavoriteRegionComplete)
                .then((res) => res.data)
                .catch((err) => {
                    console.log("err>>>", err);
                    return { complete: false };
                });
            return result;
        },
        { enabled },
    );
};

export const initialFavoriteRegionFilter: IFavoriteRegionFilter = {
    regions: [],
    categories: [],
    floor: null,
};
